import { useLanguage } from '../contexts/LanguageContext';
import { AlertTriangle, FileText, Scale, Shield, Globe, ShoppingBag, Ban, CheckCircle } from 'lucide-react';

export default function TermsPage() {
  const { language } = useLanguage();

  const content = {
    en: {
      title: 'Terms & Conditions',
      lastUpdated: 'Last Updated: December 2024',
      intro: 'Welcome to PEP-T.COM. By accessing and using this website, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, you must discontinue use of this website immediately.',
      sections: [
        {
          icon: Scale,
          title: 'Legal Classification & Regulatory Status',
          content: `PEP-T.COM operates as a supplier of research chemicals and peptides intended exclusively for in-vitro laboratory research purposes.

**IMPORTANT LEGAL NOTICE:**

Our products are classified as research chemicals under the Toxic Substances Control Act (TSCA). Pursuant to Section 5(h)(3) of TSCA (15 U.S.C. § 2604) and EPA regulations at 40 CFR 720.36, substances manufactured or imported in small quantities solely for research and development are exempt from pre-manufacture notification requirements when:

• The substances are used solely for scientific experimentation, analysis, or chemical research on, or analysis of, such substance or mixture
• The substances are handled by or under the supervision of a technically qualified individual
• Recipients receive written notice of any health or environmental risks

Our products are NOT classified as:
• Drugs or pharmaceuticals under the Federal Food, Drug, and Cosmetic Act (21 U.S.C. § 321)
• Medical devices
• Dietary supplements
• Cosmetics intended for human use
• Food additives

The purchaser assumes full responsibility to verify that products purchased from PEP-T.COM are approved for use under TSCA and all applicable regulations in their jurisdiction.`,
        },
        {
          icon: AlertTriangle,
          title: 'Product Use Disclaimer',
          content: `ALL PRODUCTS ARE SOLD FOR IN-VITRO RESEARCH PURPOSES ONLY.

By purchasing from PEP-T.COM, you expressly acknowledge and agree that:

1. Products are intended SOLELY for in-vitro laboratory research purposes
2. Products are NOT for human consumption, veterinary use, cosmetic application, or any therapeutic purpose
3. Products have NOT been evaluated or approved by the FDA for any use
4. Products are NOT sterile and have NOT been tested for safety or efficacy in humans or animals
5. The purchaser will properly test, use, and handle all products in strict compliance with:
   • Their institution's safety protocols
   • All applicable local, state, federal, and international laws
   • Good laboratory practices (GLP)

The purchaser expressly represents and warrants that any material produced with products from PEP-T.COM shall not be adulterated or misbranded within the meaning of the Federal Food, Drug, and Cosmetic Act (21 U.S.C. § 331 et seq.) and shall not be materials which may not, under Sections 404, 505, or 512 of the Act, be introduced into interstate commerce.`,
        },
        {
          icon: Shield,
          title: 'Age Requirement & Eligibility',
          content: `YOU MUST BE AT LEAST 21 YEARS OF AGE TO USE THIS WEBSITE AND PURCHASE PRODUCTS.

By using this website, you represent and warrant that:

• You are at least 21 years of age
• You are legally capable of entering into binding contracts
• You are either a qualified researcher, laboratory professional, or acting on behalf of a qualified research institution
• You have the legal authority to accept these Terms and Conditions on behalf of yourself or the entity you represent
• You are not located in a jurisdiction where the sale, purchase, or use of these products is prohibited

PEP-T.COM reserves the right to verify eligibility and refuse service to any individual or entity that does not meet these requirements.`,
        },
        {
          icon: Globe,
          title: 'Export Only Policy',
          content: `PEP-T.COM sells products EXCLUSIVELY FOR EXPORT outside of the United States.

• Products are NOT available for domestic sale within the United States
• All purchasers must provide a valid international shipping address
• Purchasers are solely responsible for compliance with all import regulations, customs requirements, and applicable laws in their destination country
• PEP-T.COM makes no representations regarding the legality of importing any product into any particular country

Any attempt to circumvent this export-only policy through the use of freight forwarders, re-shipping services, or other means to ultimately receive products within the United States is strictly prohibited and will result in immediate order cancellation without refund.`,
        },
        {
          icon: Ban,
          title: 'Prohibited Uses',
          content: `The following uses of products purchased from PEP-T.COM are STRICTLY PROHIBITED:

• Human consumption or administration by any route
• Veterinary or animal use
• Use as food additives or dietary supplements
• Cosmetic applications intended for human use
• Any diagnostic, therapeutic, or medical purpose
• Manufacturing of products intended for human or animal consumption
• Resale for non-research purposes
• Any use that violates applicable laws or regulations

Violation of these prohibitions may result in immediate termination of your account, legal action, and reporting to appropriate regulatory authorities.`,
        },
        {
          icon: ShoppingBag,
          title: 'Sales Terms',
          content: `**ALL SALES ARE FINAL**

Due to the nature of research chemicals and the strict handling requirements, we cannot accept returns, exchanges, or refunds except in cases of:

• Incorrect product shipped (must be reported within 48 hours of delivery)
• Product damaged during shipping (photographic evidence required)
• Significant deviation from Certificate of Analysis specifications (laboratory verification required)

**Payment Terms:**
• All prices are quoted in US Dollars
• Payment is required in full before shipment
• We accept major credit cards and cryptocurrency

**Shipping:**
• Products are shipped via tracked courier services
• Shipping times vary by destination
• Customer is responsible for any customs duties, taxes, or fees
• Risk of loss transfers to purchaser upon delivery to carrier`,
        },
        {
          icon: FileText,
          title: 'Intellectual Property',
          content: `All content on this website, including but not limited to text, graphics, logos, images, product descriptions, and software, is the property of PEP-T.COM or its content suppliers and is protected by United States and international copyright, trademark, and other intellectual property laws.

You may not:
• Reproduce, duplicate, copy, sell, resell, or exploit any portion of this website without express written permission
• Use any meta tags or other hidden text utilizing PEP-T.COM's name or trademarks
• Mirror or frame any page without prior written consent

Limited license is granted to access and use this website for personal, non-commercial purposes related to purchasing products for legitimate research purposes.`,
        },
        {
          icon: Shield,
          title: 'Limitation of Liability & Indemnification',
          content: `TO THE MAXIMUM EXTENT PERMITTED BY LAW:

PEP-T.COM, its officers, directors, employees, agents, and suppliers shall NOT be liable for any direct, indirect, incidental, special, consequential, or punitive damages arising from:

• Use or inability to use products purchased from us
• Any adverse effects or outcomes resulting from product use
• Unauthorized access to or alteration of your data
• Any matter relating to this website or products

THE AGGREGATE LIABILITY OF PEP-T.COM SHALL NOT EXCEED THE PURCHASE PRICE OF THE SPECIFIC PRODUCT(S) GIVING RISE TO THE CLAIM.

**Indemnification:**
You agree to indemnify, defend, and hold harmless PEP-T.COM and its affiliates from any claims, damages, losses, liabilities, and expenses (including attorneys' fees) arising from:
• Your use of products
• Your violation of these Terms
• Your violation of any third-party rights
• Your violation of any applicable laws`,
        },
      ],
      contact: {
        title: 'Contact Information',
        text: 'For questions regarding these Terms and Conditions, please contact:',
        email: 'support@pep-t.com',
        location: 'Boca Raton, Florida, USA',
      },
      agreement: 'By using this website and/or purchasing products from PEP-T.COM, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions in their entirety.',
    },
    pt: {
      title: 'Termos e Condições',
      lastUpdated: 'Última Atualização: Dezembro 2024',
      intro: 'Bem-vindo ao PEP-T.COM. Ao acessar e usar este site, você reconhece que leu, entendeu e concorda em estar vinculado a estes Termos e Condições. Se você não concordar com qualquer parte destes termos, deve interromper imediatamente o uso deste site.',
      sections: [
        {
          icon: Scale,
          title: 'Classificação Legal e Status Regulatório',
          content: `A PEP-T.COM opera como fornecedora de produtos químicos de pesquisa e peptídeos destinados exclusivamente para fins de pesquisa laboratorial in-vitro.

**AVISO LEGAL IMPORTANTE:**

Nossos produtos são classificados como produtos químicos de pesquisa sob a Lei de Controle de Substâncias Tóxicas (TSCA). De acordo com a Seção 5(h)(3) da TSCA (15 U.S.C. § 2604) e os regulamentos da EPA em 40 CFR 720.36, substâncias fabricadas ou importadas em pequenas quantidades exclusivamente para pesquisa e desenvolvimento estão isentas dos requisitos de notificação de pré-fabricação quando:

• As substâncias são usadas exclusivamente para experimentação científica, análise ou pesquisa química
• As substâncias são manuseadas por ou sob supervisão de um indivíduo tecnicamente qualificado
• Os destinatários recebem aviso por escrito de quaisquer riscos à saúde ou ao meio ambiente

Nossos produtos NÃO são classificados como:
• Medicamentos ou produtos farmacêuticos sob a Lei Federal de Alimentos, Medicamentos e Cosméticos (21 U.S.C. § 321)
• Dispositivos médicos
• Suplementos alimentares
• Cosméticos destinados ao uso humano
• Aditivos alimentares

O comprador assume total responsabilidade de verificar se os produtos adquiridos da PEP-T.COM estão aprovados para uso sob a TSCA e todas as regulamentações aplicáveis em sua jurisdição.`,
        },
        {
          icon: AlertTriangle,
          title: 'Aviso de Uso do Produto',
          content: `TODOS OS PRODUTOS SÃO VENDIDOS EXCLUSIVAMENTE PARA FINS DE PESQUISA IN-VITRO.

Ao comprar da PEP-T.COM, você expressamente reconhece e concorda que:

1. Os produtos são destinados EXCLUSIVAMENTE para fins de pesquisa laboratorial in-vitro
2. Os produtos NÃO são para consumo humano, uso veterinário, aplicação cosmética ou qualquer propósito terapêutico
3. Os produtos NÃO foram avaliados ou aprovados pela FDA para qualquer uso
4. Os produtos NÃO são estéreis e NÃO foram testados quanto à segurança ou eficácia em humanos ou animais
5. O comprador testará, usará e manuseará adequadamente todos os produtos em estrita conformidade com:
   • Os protocolos de segurança de sua instituição
   • Todas as leis locais, estaduais, federais e internacionais aplicáveis
   • Boas práticas de laboratório (BPL)

O comprador expressamente representa e garante que qualquer material produzido com produtos da PEP-T.COM não será adulterado ou rotulado incorretamente nos termos da Lei Federal de Alimentos, Medicamentos e Cosméticos (21 U.S.C. § 331 e seguintes).`,
        },
        {
          icon: Shield,
          title: 'Requisito de Idade e Elegibilidade',
          content: `VOCÊ DEVE TER PELO MENOS 21 ANOS DE IDADE PARA USAR ESTE SITE E COMPRAR PRODUTOS.

Ao usar este site, você representa e garante que:

• Você tem pelo menos 21 anos de idade
• Você é legalmente capaz de celebrar contratos vinculantes
• Você é um pesquisador qualificado, profissional de laboratório ou está agindo em nome de uma instituição de pesquisa qualificada
• Você tem autoridade legal para aceitar estes Termos e Condições em seu nome ou da entidade que representa
• Você não está localizado em uma jurisdição onde a venda, compra ou uso destes produtos é proibido

A PEP-T.COM reserva-se o direito de verificar a elegibilidade e recusar serviço a qualquer indivíduo ou entidade que não atenda a estes requisitos.`,
        },
        {
          icon: Globe,
          title: 'Política de Exportação Exclusiva',
          content: `A PEP-T.COM vende produtos EXCLUSIVAMENTE PARA EXPORTAÇÃO fora dos Estados Unidos.

• Os produtos NÃO estão disponíveis para venda doméstica nos Estados Unidos
• Todos os compradores devem fornecer um endereço de entrega internacional válido
• Os compradores são os únicos responsáveis pelo cumprimento de todas as regulamentações de importação, requisitos alfandegários e leis aplicáveis em seu país de destino
• A PEP-T.COM não faz representações sobre a legalidade de importar qualquer produto para qualquer país específico

Qualquer tentativa de contornar esta política de exportação exclusiva através do uso de transitários, serviços de reenvio ou outros meios para receber produtos nos Estados Unidos é estritamente proibida e resultará em cancelamento imediato do pedido sem reembolso.`,
        },
        {
          icon: Ban,
          title: 'Usos Proibidos',
          content: `Os seguintes usos dos produtos adquiridos da PEP-T.COM são ESTRITAMENTE PROIBIDOS:

• Consumo humano ou administração por qualquer via
• Uso veterinário ou animal
• Uso como aditivos alimentares ou suplementos dietéticos
• Aplicações cosméticas destinadas ao uso humano
• Qualquer propósito diagnóstico, terapêutico ou médico
• Fabricação de produtos destinados ao consumo humano ou animal
• Revenda para fins não relacionados à pesquisa
• Qualquer uso que viole leis ou regulamentos aplicáveis

A violação dessas proibições pode resultar em encerramento imediato de sua conta, ação legal e denúncia às autoridades reguladoras apropriadas.`,
        },
        {
          icon: ShoppingBag,
          title: 'Termos de Venda',
          content: `**TODAS AS VENDAS SÃO FINAIS**

Devido à natureza dos produtos químicos de pesquisa e aos rigorosos requisitos de manuseio, não podemos aceitar devoluções, trocas ou reembolsos, exceto em casos de:

• Produto incorreto enviado (deve ser relatado em até 48 horas após a entrega)
• Produto danificado durante o envio (evidência fotográfica necessária)
• Desvio significativo das especificações do Certificado de Análise (verificação laboratorial necessária)

**Termos de Pagamento:**
• Todos os preços são cotados em Dólares Americanos
• O pagamento integral é necessário antes do envio
• Aceitamos os principais cartões de crédito e criptomoeda

**Envio:**
• Os produtos são enviados por serviços de courier rastreados
• Os prazos de entrega variam por destino
• O cliente é responsável por quaisquer taxas alfandegárias, impostos ou taxas
• O risco de perda é transferido ao comprador na entrega ao transportador`,
        },
        {
          icon: FileText,
          title: 'Propriedade Intelectual',
          content: `Todo o conteúdo deste site, incluindo, mas não limitado a, textos, gráficos, logotipos, imagens, descrições de produtos e software, é propriedade da PEP-T.COM ou de seus fornecedores de conteúdo e é protegido por leis de direitos autorais, marcas registradas e outras leis de propriedade intelectual dos Estados Unidos e internacionais.

Você não pode:
• Reproduzir, duplicar, copiar, vender, revender ou explorar qualquer parte deste site sem permissão expressa por escrito
• Usar quaisquer meta tags ou outro texto oculto utilizando o nome ou marcas registradas da PEP-T.COM
• Espelhar ou enquadrar qualquer página sem consentimento prévio por escrito

É concedida licença limitada para acessar e usar este site para fins pessoais e não comerciais relacionados à compra de produtos para fins de pesquisa legítimos.`,
        },
        {
          icon: Shield,
          title: 'Limitação de Responsabilidade e Indenização',
          content: `NA EXTENSÃO MÁXIMA PERMITIDA POR LEI:

A PEP-T.COM, seus diretores, funcionários, agentes e fornecedores NÃO serão responsáveis por quaisquer danos diretos, indiretos, incidentais, especiais, consequenciais ou punitivos decorrentes de:

• Uso ou incapacidade de usar produtos adquiridos de nós
• Quaisquer efeitos adversos ou resultados decorrentes do uso do produto
• Acesso não autorizado ou alteração de seus dados
• Qualquer assunto relacionado a este site ou produtos

A RESPONSABILIDADE AGREGADA DA PEP-T.COM NÃO EXCEDERÁ O PREÇO DE COMPRA DO(S) PRODUTO(S) ESPECÍFICO(S) QUE DEU(RAM) ORIGEM À REIVINDICAÇÃO.

**Indenização:**
Você concorda em indenizar, defender e isentar a PEP-T.COM e suas afiliadas de quaisquer reivindicações, danos, perdas, responsabilidades e despesas (incluindo honorários advocatícios) decorrentes de:
• Seu uso dos produtos
• Sua violação destes Termos
• Sua violação de quaisquer direitos de terceiros
• Sua violação de quaisquer leis aplicáveis`,
        },
      ],
      contact: {
        title: 'Informações de Contato',
        text: 'Para perguntas sobre estes Termos e Condições, entre em contato:',
        email: 'support@pep-t.com',
        location: 'Boca Raton, Flórida, EUA',
      },
      agreement: 'Ao usar este site e/ou comprar produtos da PEP-T.COM, você reconhece que leu, entendeu e concorda em estar vinculado a estes Termos e Condições em sua totalidade.',
    },
    es: {
      title: 'Términos y Condiciones',
      lastUpdated: 'Última Actualización: Diciembre 2024',
      intro: 'Bienvenido a PEP-T.COM. Al acceder y usar este sitio web, usted reconoce que ha leído, entendido y acepta estar sujeto a estos Términos y Condiciones. Si no está de acuerdo con alguna parte de estos términos, debe dejar de usar este sitio web inmediatamente.',
      sections: [
        {
          icon: Scale,
          title: 'Clasificación Legal y Estado Regulatorio',
          content: `PEP-T.COM opera como proveedor de productos químicos de investigación y péptidos destinados exclusivamente para fines de investigación de laboratorio in-vitro.

**AVISO LEGAL IMPORTANTE:**

Nuestros productos están clasificados como productos químicos de investigación bajo la Ley de Control de Sustancias Tóxicas (TSCA). De acuerdo con la Sección 5(h)(3) de TSCA (15 U.S.C. § 2604) y las regulaciones de la EPA en 40 CFR 720.36, las sustancias fabricadas o importadas en pequeñas cantidades exclusivamente para investigación y desarrollo están exentas de los requisitos de notificación de pre-fabricación cuando:

• Las sustancias se usan únicamente para experimentación científica, análisis o investigación química
• Las sustancias son manejadas por o bajo la supervisión de un individuo técnicamente calificado
• Los destinatarios reciben aviso por escrito de cualquier riesgo para la salud o el medio ambiente

Nuestros productos NO están clasificados como:
• Medicamentos o productos farmacéuticos bajo la Ley Federal de Alimentos, Medicamentos y Cosméticos (21 U.S.C. § 321)
• Dispositivos médicos
• Suplementos dietéticos
• Cosméticos destinados al uso humano
• Aditivos alimentarios

El comprador asume total responsabilidad de verificar que los productos adquiridos de PEP-T.COM están aprobados para uso bajo TSCA y todas las regulaciones aplicables en su jurisdicción.`,
        },
        {
          icon: AlertTriangle,
          title: 'Aviso de Uso del Producto',
          content: `TODOS LOS PRODUCTOS SE VENDEN ÚNICAMENTE PARA FINES DE INVESTIGACIÓN IN-VITRO.

Al comprar de PEP-T.COM, usted expresamente reconoce y acepta que:

1. Los productos están destinados ÚNICAMENTE para fines de investigación de laboratorio in-vitro
2. Los productos NO son para consumo humano, uso veterinario, aplicación cosmética o cualquier propósito terapéutico
3. Los productos NO han sido evaluados ni aprobados por la FDA para ningún uso
4. Los productos NO son estériles y NO han sido probados para seguridad o eficacia en humanos o animales
5. El comprador probará, usará y manejará adecuadamente todos los productos en estricto cumplimiento con:
   • Los protocolos de seguridad de su institución
   • Todas las leyes locales, estatales, federales e internacionales aplicables
   • Buenas prácticas de laboratorio (BPL)

El comprador expresamente representa y garantiza que cualquier material producido con productos de PEP-T.COM no será adulterado ni mal etiquetado según los términos de la Ley Federal de Alimentos, Medicamentos y Cosméticos (21 U.S.C. § 331 y siguientes).`,
        },
        {
          icon: Shield,
          title: 'Requisito de Edad y Elegibilidad',
          content: `DEBE TENER AL MENOS 21 AÑOS DE EDAD PARA USAR ESTE SITIO WEB Y COMPRAR PRODUCTOS.

Al usar este sitio web, usted representa y garantiza que:

• Tiene al menos 21 años de edad
• Es legalmente capaz de celebrar contratos vinculantes
• Es un investigador calificado, profesional de laboratorio o actúa en nombre de una institución de investigación calificada
• Tiene la autoridad legal para aceptar estos Términos y Condiciones en su nombre o de la entidad que representa
• No está ubicado en una jurisdicción donde la venta, compra o uso de estos productos está prohibido

PEP-T.COM se reserva el derecho de verificar la elegibilidad y rechazar el servicio a cualquier individuo o entidad que no cumpla con estos requisitos.`,
        },
        {
          icon: Globe,
          title: 'Política de Solo Exportación',
          content: `PEP-T.COM vende productos EXCLUSIVAMENTE PARA EXPORTACIÓN fuera de Estados Unidos.

• Los productos NO están disponibles para venta doméstica en Estados Unidos
• Todos los compradores deben proporcionar una dirección de envío internacional válida
• Los compradores son los únicos responsables del cumplimiento de todas las regulaciones de importación, requisitos aduaneros y leyes aplicables en su país de destino
• PEP-T.COM no hace representaciones sobre la legalidad de importar cualquier producto a cualquier país específico

Cualquier intento de eludir esta política de solo exportación mediante el uso de transitarios, servicios de reenvío u otros medios para recibir productos en Estados Unidos está estrictamente prohibido y resultará en la cancelación inmediata del pedido sin reembolso.`,
        },
        {
          icon: Ban,
          title: 'Usos Prohibidos',
          content: `Los siguientes usos de los productos adquiridos de PEP-T.COM están ESTRICTAMENTE PROHIBIDOS:

• Consumo humano o administración por cualquier vía
• Uso veterinario o animal
• Uso como aditivos alimentarios o suplementos dietéticos
• Aplicaciones cosméticas destinadas al uso humano
• Cualquier propósito diagnóstico, terapéutico o médico
• Fabricación de productos destinados al consumo humano o animal
• Reventa para fines no relacionados con la investigación
• Cualquier uso que viole las leyes o regulaciones aplicables

La violación de estas prohibiciones puede resultar en la terminación inmediata de su cuenta, acciones legales y denuncia a las autoridades reguladoras apropiadas.`,
        },
        {
          icon: ShoppingBag,
          title: 'Términos de Venta',
          content: `**TODAS LAS VENTAS SON FINALES**

Debido a la naturaleza de los productos químicos de investigación y los estrictos requisitos de manejo, no podemos aceptar devoluciones, cambios o reembolsos, excepto en casos de:

• Producto incorrecto enviado (debe reportarse dentro de 48 horas de la entrega)
• Producto dañado durante el envío (se requiere evidencia fotográfica)
• Desviación significativa de las especificaciones del Certificado de Análisis (se requiere verificación de laboratorio)

**Términos de Pago:**
• Todos los precios están cotizados en Dólares Estadounidenses
• El pago completo es requerido antes del envío
• Aceptamos las principales tarjetas de crédito y criptomonedas

**Envío:**
• Los productos se envían mediante servicios de courier rastreados
• Los tiempos de envío varían según el destino
• El cliente es responsable de cualquier impuesto aduanero, tasas o cargos
• El riesgo de pérdida se transfiere al comprador en la entrega al transportista`,
        },
        {
          icon: FileText,
          title: 'Propiedad Intelectual',
          content: `Todo el contenido de este sitio web, incluyendo pero no limitado a texto, gráficos, logotipos, imágenes, descripciones de productos y software, es propiedad de PEP-T.COM o sus proveedores de contenido y está protegido por las leyes de derechos de autor, marcas registradas y otras leyes de propiedad intelectual de Estados Unidos e internacionales.

Usted no puede:
• Reproducir, duplicar, copiar, vender, revender o explotar cualquier parte de este sitio web sin permiso expreso por escrito
• Usar cualquier meta tag u otro texto oculto utilizando el nombre o marcas registradas de PEP-T.COM
• Reflejar o enmarcar cualquier página sin consentimiento previo por escrito

Se otorga una licencia limitada para acceder y usar este sitio web para fines personales no comerciales relacionados con la compra de productos para fines de investigación legítimos.`,
        },
        {
          icon: Shield,
          title: 'Limitación de Responsabilidad e Indemnización',
          content: `EN LA MÁXIMA MEDIDA PERMITIDA POR LA LEY:

PEP-T.COM, sus directores, empleados, agentes y proveedores NO serán responsables de ningún daño directo, indirecto, incidental, especial, consecuente o punitivo que surja de:

• Uso o incapacidad de usar productos adquiridos de nosotros
• Cualquier efecto adverso o resultado del uso del producto
• Acceso no autorizado o alteración de sus datos
• Cualquier asunto relacionado con este sitio web o productos

LA RESPONSABILIDAD TOTAL DE PEP-T.COM NO EXCEDERÁ EL PRECIO DE COMPRA DEL(LOS) PRODUCTO(S) ESPECÍFICO(S) QUE DIO(DIERON) ORIGEN AL RECLAMO.

**Indemnización:**
Usted acepta indemnizar, defender y mantener indemne a PEP-T.COM y sus afiliados de cualquier reclamo, daño, pérdida, responsabilidad y gasto (incluidos honorarios de abogados) que surjan de:
• Su uso de los productos
• Su violación de estos Términos
• Su violación de cualquier derecho de terceros
• Su violación de cualquier ley aplicable`,
        },
      ],
      contact: {
        title: 'Información de Contacto',
        text: 'Para preguntas sobre estos Términos y Condiciones, contáctenos:',
        email: 'support@pep-t.com',
        location: 'Boca Raton, Florida, EE.UU.',
      },
      agreement: 'Al usar este sitio web y/o comprar productos de PEP-T.COM, usted reconoce que ha leído, entendido y acepta estar sujeto a estos Términos y Condiciones en su totalidad.',
    },
  };

  const t = content[language];

  return (
    <div className="min-h-screen bg-neutral-50 pt-20 lg:pt-24" data-testid="terms-page">
      {/* Header */}
      <div className="bg-neutral-900 text-white py-16">
        <div className="container-custom">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-8 h-8 text-red-500" />
            <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {t.title}
            </h1>
          </div>
          <p className="text-neutral-400">{t.lastUpdated}</p>
        </div>
      </div>

      <div className="container-custom py-12">
        {/* Introduction */}
        <div className="bg-white border border-neutral-200 p-6 mb-8 rounded-sm">
          <p className="text-neutral-700 leading-relaxed">{t.intro}</p>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {t.sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <div key={index} className="bg-white border border-neutral-200 rounded-sm overflow-hidden">
                <div className="bg-neutral-50 border-b border-neutral-200 px-6 py-4 flex items-center gap-3">
                  <div className="p-2 bg-red-50 rounded-sm">
                    <Icon className="w-5 h-5 text-red-600" />
                  </div>
                  <h2 className="text-xl font-bold text-neutral-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    {section.title}
                  </h2>
                </div>
                <div className="p-6">
                  <div className="prose prose-neutral max-w-none">
                    {section.content.split('\n\n').map((paragraph, pIndex) => (
                      <div key={pIndex} className="mb-4 last:mb-0">
                        {paragraph.startsWith('**') ? (
                          <p className="whitespace-pre-wrap text-neutral-700 leading-relaxed" 
                             dangerouslySetInnerHTML={{
                               __html: paragraph.replace(/\*\*(.*?)\*\*/g, '<strong class="text-neutral-900">$1</strong>')
                             }} 
                          />
                        ) : paragraph.startsWith('•') ? (
                          <ul className="list-disc list-inside space-y-1 text-neutral-700">
                            {paragraph.split('\n').map((item, iIndex) => (
                              <li key={iIndex}>{item.replace('• ', '')}</li>
                            ))}
                          </ul>
                        ) : paragraph.match(/^\d\./) ? (
                          <ol className="list-decimal list-inside space-y-1 text-neutral-700">
                            {paragraph.split('\n').map((item, iIndex) => (
                              <li key={iIndex}>{item.replace(/^\d\.\s*/, '')}</li>
                            ))}
                          </ol>
                        ) : (
                          <p className="text-neutral-700 leading-relaxed whitespace-pre-wrap"
                             dangerouslySetInnerHTML={{
                               __html: paragraph.replace(/\*\*(.*?)\*\*/g, '<strong class="text-neutral-900">$1</strong>')
                             }} 
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Contact */}
        <div className="bg-neutral-900 text-white p-6 mt-8 rounded-sm">
          <h3 className="text-lg font-bold mb-3">{t.contact.title}</h3>
          <p className="text-neutral-300 mb-4">{t.contact.text}</p>
          <div className="space-y-2">
            <p className="text-neutral-200">
              <span className="text-red-400">Email:</span> {t.contact.email}
            </p>
            <p className="text-neutral-200">
              <span className="text-red-400">{language === 'pt' ? 'Localização:' : language === 'es' ? 'Ubicación:' : 'Location:'}</span> {t.contact.location}
            </p>
          </div>
        </div>

        {/* Agreement */}
        <div className="bg-red-50 border border-red-200 p-6 mt-8 rounded-sm flex items-start gap-4">
          <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-900 font-medium">{t.agreement}</p>
        </div>
      </div>
    </div>
  );
}
